import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map, take, tap } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { isEqual } from 'lodash';

import { User } from 'src/app/models/user';
import { UserService } from 'src/app/services/user.service';
import { SettingsService } from 'src/app/services/settings.service';
import { SettingsNotificationService } from 'src/app/settings/shared/services/settings-notifications.service';
import { PanelData } from '../../settings.constants';

@UntilDestroy()
@Component({
  selector: 'app-user-settings-sessions',
  templateUrl: './user-settings-sessions.component.html',
  styleUrls: ['./user-settings-sessions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsSessionsComponent implements OnInit {
  @Input() form!: UntypedFormGroup;
  @Input() isMobile = false;
  @Input() panelData?: PanelData;
  @Output() goBack = new EventEmitter<void>();
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();

  settingsForm?: UntypedFormGroup;
  user?: User;
  initialFormValue: Record<string, unknown> | null = null;
  formChanged$ = new BehaviorSubject<boolean>(false);
  private readonly isLoadingSubject$ = new BehaviorSubject<boolean>(false);

  readonly viewModel$ = combineLatest([this.formChanged$, this.isLoadingSubject$]).pipe(
    map(([formChanged, isLoading]) => ({ formChanged, isLoading })),
    untilDestroyed(this),
  );

  constructor(
    private userService: UserService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private settingsNotificationService: SettingsNotificationService,
  ) {}

  ngOnInit(): void {
    this.settingsForm = this.form?.controls.settings as UntypedFormGroup;
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.initForm();
    });
  }

  private initForm(): void {
    this.form.patchValue(
      {
        settings: {
          crEnablePublicRecordings:
            this.user?.institution?.settings?.crEnablePublicRecordings ?? false,
        },
      },
      { emitEvent: false },
    );

    this.initialFormValue = this.form.value;

    this.form.valueChanges
      .pipe(
        map(() => !isEqual(this.initialFormValue, this.form.value)),
        tap((hasChanged) => this.formChangedEvent.emit(hasChanged)),
        untilDestroyed(this),
      )
      .subscribe(this.formChanged$);
  }

  submitChanges(): void {
    if (!this.user || this.isLoadingSubject$.value) {
      return;
    }

    this.isLoadingSubject$.next(true);
    const updatedUser = {
      ...this.user,
      institution: {
        ...this.user?.institution,
        settings: {
          ...this.user?.institution.settings,
          ...this.settingsForm?.value,
        },
      },
    } as User;

    this.userService
      .updateInstitution(updatedUser)
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        error: () => {
          this.isLoadingSubject$.next(false);
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
        },
        complete: () => {
          this.isLoadingSubject$.next(false);
          this.formChanged$.next(false);
          this.formChangedEvent.emit(false);
          this.initialFormValue = this.form.value;
          const userFromService = this.userService.user.getValue();
          this.userService.user.next({
            ...userFromService,
            user: updatedUser,
          });
          this.settingsNotificationService.showSuccessSettingsUpdateNotification();
        },
      });
  }

  cancelChanges(): void {
    if (!this.initialFormValue) {
      return;
    }

    this.form.patchValue(this.initialFormValue, { emitEvent: false });
    this.formChanged$.next(false);
    this.formChangedEvent.emit(false);
  }
}
